import {
  prisma,
  reportingPrisma,
  Privacies,
  Visibility,
  type Prisma,
  Order,
  OrderItem,
  DTCMOrderStatus,
  OrderStatus,
  BookableItemType,
} from "client";
import { getLanguages } from "services/language";
import { generateEventNumber } from "services/event";
import {
  createNewPriceCategoryWithTickets,
  findTicketCategoryByDtcmSections,
  linkTicketCategoryToDtcmSections,
  syncCategoryCapacity,
  syncSections,
  syncTicketPrices,
} from "./heper";

type DtcmOrderLineItem = {
  priceTypeCode?: string;
  PriceTypeCode?: string;
  barcode?: string;
  Barcode?: string;
};

type DtcmOrderLine = {
  orderLineItems?: DtcmOrderLineItem[];
  OrderLineItems?: DtcmOrderLineItem[];
};

type DtcmPurchaseOrderResponse = {
  id?: string | number;
  orderId?: string | number;
  OrderId?: string | number;
  orderLines?: DtcmOrderLine[];
  OrderLines?: DtcmOrderLine[];
  account?: number;
  Account?: number;
  aFile?: string;
  AFile?: string;
};

function getDtcmOrderLines(data: DtcmPurchaseOrderResponse): DtcmOrderLine[] {
  return data.orderLines ?? data.OrderLines ?? [];
}

function getDtcmOrderLineItems(
  orderLines: DtcmOrderLine[]
): DtcmOrderLineItem[] {
  return orderLines.flatMap(
    (orderLine) => orderLine.orderLineItems ?? orderLine.OrderLineItems ?? []
  );
}

const DTCM_CUSTOMER_DEFAULTS = {
  firstName: "Guest",
  lastName: "Guest",
  nationality: "UAE",
} as const;

type DtcmCustomerInput = {
  email: string;
  firstname?: string | null;
  lastname?: string | null;
  nationality?: string | null;
};

type DtcmCustomerExisting = {
  firstName?: string | null;
  lastName?: string | null;
  nationality?: string | null;
  email?: string | null;
};

function isValidDtcmNationality(nationality: string): boolean {
  const trimmed = nationality.trim();
  if (!trimmed) {
    return false;
  }

  // ISO 3166-1 alpha-2 (e.g. AE, JO)
  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    return true;
  }

  // Country / demonym style values (e.g. UAE, Jordan, Egyptian)
  if (/^[A-Za-z][A-Za-z\s\-'.]{1,56}$/.test(trimmed)) {
    return true;
  }

  return false;
}

function resolveDtcmCustomerFields(
  customerData: DtcmCustomerInput,
  existingCustomer?: DtcmCustomerExisting | null
) {
  const email =
    customerData.email?.trim() || existingCustomer?.email?.trim() || "";

  const firstName =
    customerData.firstname?.trim() ||
    existingCustomer?.firstName?.trim() ||
    DTCM_CUSTOMER_DEFAULTS.firstName;

  const lastName =
    customerData.lastname?.trim() ||
    existingCustomer?.lastName?.trim() ||
    DTCM_CUSTOMER_DEFAULTS.lastName;

  const rawNationality =
    customerData.nationality?.trim() ||
    existingCustomer?.nationality?.trim() ||
    "";

  const nationality = isValidDtcmNationality(rawNationality)
    ? rawNationality
    : DTCM_CUSTOMER_DEFAULTS.nationality;

  return { email, firstName, lastName, nationality };
}

export const createDTCMEvent = async (
  data: any,
  whiteLabelId: string,
  userId: string,
  languagesIds?: string[]
) => {
  try {
    const allLanguages = await getLanguages();
    // undefined = legacy callers (default all); [] = explicit empty → reject below
    const selectedLanguageIds =
      languagesIds === undefined
        ? allLanguages.map((lang) => lang.id)
        : languagesIds;
    const languages = allLanguages.filter((lang) =>
      selectedLanguageIds.includes(lang.id)
    );
    if (languages.length === 0) {
      throw new Error("No valid event languages selected");
    }
    const resolvedLanguageIds = languages.map((lang) => lang.id);

    const event = await prisma.dTCMPerformance.create({
      data: {
        performanceCode: data.performanceCode,
        productId: data.productId,
        name: data.name,
        startDate: new Date(data.startDate),
        endDate: new Date(data.endDate),
        webSaleStartDate: new Date(data.webSaleStartDate),
        webSaleEndDate: new Date(data.webSaleEndDate),
        showCode: data.showCode,
        venueCode: data.venueCode,
        createdById: userId,
        events: {
          create: {
            number: await generateEventNumber(whiteLabelId),
            createdBy: {
              connect: { id: userId },
            },
            currency: "AED",
            dtcmPerformanceCode: data.performanceCode,
            startDate: new Date(data.startDate),
            endDate: new Date(data.endDate),
            languagesIds: resolvedLanguageIds,
            whiteLabel: {
              connect: { id: whiteLabelId },
            },
            translations: {
              create: languages.map((lang) => ({
                name: data.name,
                language: { connect: { id: lang.id } },
              })),
            },
          },
        },
        dtcmPriceCategories: {
          create: data.priceCategories.map((cat: any) => ({
            dtcmId: String(cat.id),
            code: cat.code,
            name: cat.name,
            maxQtyPerTransaction: cat.maxQtyPerTransaction,
            dtcmSections: {
              create: data.sections
                .filter((s: any) => s.categoryId === cat.id)
                .map((section: any) => ({
                  code: section.code,
                  name: section.name,
                  capacity: section.capacity,
                  holds: section.holds,
                  comps: section.comps,
                  isGA: section.isGA,
                })),
            },
            dtcmTicketPrices: {
              create: data.ticketPrices
                .filter((p: any) => p.categoryId === cat.id)
                .map((price: any) => ({
                  priceTypeId: String(price.typeId),
                  typeCode: price.typeCode,
                  price: price.price,
                  name: data.priceTypes.find(
                    (pt: any) => pt.id === price.typeId
                  )?.name,
                  state: data.priceTypes.find(
                    (pt: any) => pt.id === price.typeId
                  )?.state,
                })),
            },
          })),
        },
      },
      include: {
        events: true,
        dtcmPriceCategories: {
          include: {
            dtcmSections: true,
            dtcmTicketPrices: true,
          },
        },
      },
    });
    const eventId = event.events?.[0].id;

    for (const category of event.dtcmPriceCategories) {
      const ticketCategory = await prisma.ticketCategory.create({
        data: {
          name: category.name,
          quantity: category.dtcmSections.reduce(
            (sum, sec) => sum + sec.capacity,
            0
          ),
          isSharedQuantity: true,
          eventId: eventId,
          dtcmSections: {
            connect: category.dtcmSections.map((section) => ({
              id: section.id,
            })),
          },
        },
      });

      await linkTicketCategoryToDtcmSections(
        ticketCategory.id,
        category.dtcmSections.map((section) => section.id)
      );

      // 2. Create Tickets for this category
      for (const ticketPrice of category.dtcmTicketPrices) {
        // First create the BookableItem
        const bookableItem = await prisma.bookableItem.create({
          data: {
            price: ticketPrice.price,
            type: BookableItemType.TICKET,
            eventId,
            visibility:
              ticketPrice.state == "Available"
                ? Visibility.PUBLISHED
                : Visibility.DRAFT,
            privacy: Privacies.PUBLIC,
            saleStartDate: new Date(data.webSaleStartDate),
            saleEndDate: new Date(data.webSaleEndDate),
            translations: {
              create: languages.map((lang) => ({
                name: ticketPrice.name,
                description: ticketPrice.name,
                language: { connect: { id: lang.id } },
              })),
            },
          },
        });

        // Then create the ticket with the bookableItemId
        await prisma.ticket.create({
          data: {
            eventId,
            bookableItemId: bookableItem.id,
            dtcmTicketPriceId: ticketPrice.id,
            categoryId: ticketCategory.id,
          },
        });
      }
    }

    return event;
  } catch (error: any) {
    console.log(`error creating DTCM event: ${error}`);
    throw new Error(error.message);
  }
};

export const getDTCMTicketCategories = async (eventId: string) => {
  const latestPerformance = await prisma.dTCMPerformance.findFirst({
    where: {
      events: {
        some: {
          id: eventId,
        },
      },
    },
    orderBy: {
      updatedAt: "desc",
    },
    include: {
      dtcmPriceCategories: {
        include: {
          dtcmSections: true,
          dtcmTicketPrices: {
            include: {
              tickets: {
                include: {
                  bookableItem: {
                    include: {
                      translations: true,
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  });

  return {
    result: latestPerformance?.dtcmPriceCategories,
    updatedAt: latestPerformance?.updatedAt,
  };
};

export const createDTCMOrder = async (
  data: any,
  order: Order & { orderItems: OrderItem[] }
) => {
  try {
    const existing = await prisma.dTCMOrder.findUnique({
      where: { orderId: order.id },
    });

    if (existing) {
      return prisma.dTCMOrder.update({
        where: { id: existing.id },
        data: {
          dtcmBasketId: data.id,
          status: DTCMOrderStatus.PENDING,
        },
      });
    }

    const dtcmOrder = await prisma.dTCMOrder.create({
      data: {
        dtcmBasketId: data.id,
        order: {
          connect: {
            id: order.id,
          },
        },
        dtcmOrderItems: {
          create: order.orderItems.map((item: any) => ({
            orderItem: {
              connect: { id: item.id },
            },
          })),
        },
      },
    });
    return dtcmOrder;
  } catch (error) {
    console.error("Error creating DTCM order:", error);
    throw new Error("Failed to create DTCM order");
  }
};

export const createDraftDTCMOrder = async (
  order: Order & { orderItems: OrderItem[] }
) => {
  try {
    const existing = await prisma.dTCMOrder.findUnique({
      where: { orderId: order.id },
      include: { dtcmOrderItems: true },
    });

    if (existing) {
      return existing;
    }

    const dtcmOrder = await prisma.dTCMOrder.create({
      data: {
        order: {
          connect: {
            id: order.id,
          },
        },
        dtcmOrderItems: {
          create: order.orderItems.map((item: any) => ({
            orderItem: {
              connect: { id: item.id },
            },
          })),
        },
        status: DTCMOrderStatus.DRAFT,
      },
    });
    return dtcmOrder;
  } catch (error) {
    console.error("Error creating DTCM order:", error);
    throw new Error("Failed to create DTCM order");
  }
};

export const DTCM_ORDERS_PROCESSING_BATCH_SIZE = 50;

export const getDTCMOrdersNeedingProcessing = async (
  options: { limit?: number } = {}
) => {
  const limit = options.limit ?? DTCM_ORDERS_PROCESSING_BATCH_SIZE;

  try {
    return prisma.dTCMOrder.findMany({
      where: {
        status: {
          in: [DTCMOrderStatus.PENDING, DTCMOrderStatus.DRAFT],
        },
        order: {
          status: OrderStatus.COMPLETED,
        },
      },
      orderBy: [{ order: { createdAt: "desc" } }, { createdAt: "desc" }],
      take: limit,
      include: {
        order: {
          include: {
            dtcmOrder: true,
            formResults: {
              include: {
                formResult: true,
              },
            },
            orderItems: {
              include: {
                ticket: {
                  include: {
                    category: {
                      include: {
                        dtcmSections: true,
                      },
                    },
                    dtcmTicketPrice: true,
                  },
                },
              },
            },
            event: {
              select: {
                id: true,
                whiteLabelId: true,
                dtcmPerformanceCode: true,
                startDate: true,
                timezone: true,
              },
            },
          },
        },
      },
    });
  } catch (error) {
    console.error("Error getting DTCM orders needing processing:", error);
    throw new Error("Failed to get DTCM orders needing processing");
  }
};

export const completeDTCMOrder = async (
  data: DtcmPurchaseOrderResponse,
  orderId: string,
  customerData: DtcmCustomerInput,
  dtcmCustomerRef?: string | { id: string; account?: number; aFile?: string }
) => {
  const dtcmOrderId = data.id ?? data.orderId ?? data.OrderId;
  const orderLines = getDtcmOrderLines(data);
  const customerRef =
    typeof dtcmCustomerRef === "string"
      ? { id: dtcmCustomerRef }
      : dtcmCustomerRef;

  const alreadyDone = await prisma.dTCMOrder.findFirst({
    where: { orderId },
    select: { id: true, status: true },
  });
  if (alreadyDone?.status === DTCMOrderStatus.COMPLETED) {
    return { success: true as const, skipped: true as const };
  }

  if (!dtcmOrderId) {
    throw new Error("DTCM purchase response is missing order id");
  }

  const lookupEmail = customerData.email?.trim();
  if (!lookupEmail) {
    throw new Error("DTCM customer email is required to complete order");
  }

  const prismaTx = prisma.$transaction(async (tx) => {
    const existingCustomer = await tx.dTCMCustomer.findFirst({
      where: { email: lookupEmail },
    });

    const resolvedCustomer = resolveDtcmCustomerFields(
      customerData,
      existingCustomer
    );

    const resolvedCustomerDtcmId = customerRef?.id ?? existingCustomer?.dtcmId;

    if (!resolvedCustomerDtcmId) {
      throw new Error("DTCM customer id is required to complete order");
    }

    const resolvedAccount =
      customerRef?.account ??
      existingCustomer?.account ??
      data.account ??
      data.Account ??
      0;
    const resolvedAFile =
      customerRef?.aFile ??
      existingCustomer?.aFile ??
      data.aFile ??
      data.AFile ??
      "";

    let customer;
    if (existingCustomer) {
      customer = await tx.dTCMCustomer.update({
        where: { id: existingCustomer.id },
        data: {
          dtcmId: resolvedCustomerDtcmId,
          account: resolvedAccount,
          aFile: resolvedAFile,
          firstName: resolvedCustomer.firstName,
          lastName: resolvedCustomer.lastName,
          email: resolvedCustomer.email,
          nationality: resolvedCustomer.nationality,
        },
      });
    } else {
      customer = await tx.dTCMCustomer.create({
        data: {
          dtcmId: resolvedCustomerDtcmId,
          account: resolvedAccount,
          aFile: resolvedAFile,
          firstName: resolvedCustomer.firstName,
          lastName: resolvedCustomer.lastName,
          email: resolvedCustomer.email,
          nationality: resolvedCustomer.nationality,
        },
      });
    }

    const dtcmOrder = await tx.dTCMOrder.findFirst({
      where: {
        orderId,
      },
      include: {
        dtcmOrderItems: {
          include: {
            orderItem: {
              include: {
                ticket: {
                  include: {
                    dtcmTicketPrice: true,
                  },
                },
              },
            },
          },
        },
      },
    });

    if (!dtcmOrder) {
      throw new Error(`DTCM order for Sajilni order ${orderId} not found`);
    }

    await tx.dTCMOrder.update({
      where: {
        id: dtcmOrder.id,
      },
      data: {
        status: DTCMOrderStatus.COMPLETED,
        dtcmId: String(dtcmOrderId),
        DTCMCustomer: {
          connect: {
            id: customer.id,
          },
        },
      },
    });

    const orderLineItems = getDtcmOrderLineItems(orderLines);

    const assignedCounts = new Map<string, number>();

    for (const item of orderLineItems) {
      const priceTypeCode = item.priceTypeCode ?? item.PriceTypeCode;
      const barcode = item.barcode ?? item.Barcode;
      if (!priceTypeCode) {
        continue;
      }

      const slotIndex = assignedCounts.get(priceTypeCode) ?? 0;
      assignedCounts.set(priceTypeCode, slotIndex + 1);

      const matchingItems = dtcmOrder.dtcmOrderItems.filter(
        (orderItem) =>
          orderItem.barcode === null &&
          orderItem.orderItem.ticket?.dtcmTicketPrice?.typeCode ===
            priceTypeCode
      );

      const dtcmOrderItem = matchingItems[slotIndex];

      if (dtcmOrderItem && barcode) {
        await tx.dTCMOrderItem.update({
          where: { id: dtcmOrderItem.id },
          data: {
            barcode,
            orderItem: {
              update: {
                dtcmBarcode: barcode,
              },
            },
          },
        });
      }
    }
  });

  try {
    await prismaTx;
    return { success: true };
  } catch (error) {
    console.error("Error completing DTCM order:", error);
    throw new Error("Failed to complete DTCM order");
  }
};

export const getDTCMCustomer = async (
  dtcmId: string | undefined,
  email: string
) => {
  try {
    const customer = await prisma.dTCMCustomer.findFirst({
      where: {
        OR: [...(dtcmId ? [{ dtcmId }] : []), { email }],
      },
    });
    return customer;
  } catch (error) {
    console.error("Error getting DTCM customer:", error);
    throw new Error("Failed to get DTCM customer");
  }
};

export const deleteDTCMOrder = async (dtcmBasketId: string) => {
  try {
    await prisma.$transaction(async (tx) => {
      // Delete DTCMOrderItems first
      await tx.dTCMOrderItem.deleteMany({
        where: {
          dtcmOrder: {
            dtcmBasketId: dtcmBasketId,
          },
        },
      });

      // Delete the DTCMOrder
      await tx.dTCMOrder.deleteMany({
        where: {
          dtcmBasketId: dtcmBasketId,
        },
      });
    });
    return { success: true };
  } catch (error) {
    console.error("Error deleting DTCM order:", error);
    throw new Error("Failed to delete DTCM order");
  }
};

export const syncDTCMEvent = async (
  data: any,
  whiteLabelId: string,
  userId: string,
  eventId: string
) => {
  const existingPerformance = await prisma.dTCMPerformance.findFirst({
    where: {
      performanceCode: data.performanceCode,
      events: { some: { id: eventId } },
    },
    include: {
      dtcmPriceCategories: {
        include: {
          dtcmSections: true,
          dtcmTicketPrices: true,
        },
      },
      events: true,
    },
  });

  const languages = await getLanguages();
  const performance = await prisma.dTCMPerformance.findFirst({
    where: {
      performanceCode: data.performanceCode,
      events: {
        some: { id: eventId },
      },
    },
  });

  if (!performance) {
    throw new Error("Performance not found");
  }
  await prisma.dTCMPerformance.update({
    where: {
      id: performance.id,
    },
    data: {
      updatedById: userId,
    },
  });
  for (const category of data.priceCategories) {
    const existingCategory = existingPerformance?.dtcmPriceCategories?.find(
      (cat: any) => cat.dtcmId === String(category.id)
    );

    if (!existingCategory) {
      await createNewPriceCategoryWithTickets({
        category,
        data,
        eventId,
        languages,
        whiteLabelId,
        userId,
        performanceId: performance.id,
      });
      continue;
    }

    await syncCategoryCapacity(existingCategory, category, data);
    await syncSections(existingCategory, category, data);
    await syncTicketPrices(
      existingCategory,
      category,
      data,
      eventId,
      languages
    );

    const updatedCategory = await prisma.dTCMPriceCategory.findUnique({
      where: { id: existingCategory.id },
      include: { dtcmSections: true },
    });
    const ticketCategory = await findTicketCategoryByDtcmSections(
      existingCategory.dtcmSections.map((section: { id: string }) => section.id)
    );

    if (ticketCategory && updatedCategory?.dtcmSections.length) {
      await linkTicketCategoryToDtcmSections(
        ticketCategory.id,
        updatedCategory.dtcmSections.map((section) => section.id)
      );
    }
  }

  return await prisma.dTCMPerformance.findFirst({
    where: {
      performanceCode: data.performanceCode,
      events: {
        some: {
          id: eventId,
        },
      },
    },
    include: { events: true },
  });
};

export const getDTCMPendingOrders = async () => {
  try {
    const orders = await prisma.dTCMOrder.findMany({
      where: {
        status: DTCMOrderStatus.PENDING,
        order: {
          status: OrderStatus.COMPLETED,
        },
      },
      include: {
        order: {
          include: {
            dtcmOrder: true,
            formResults: {
              include: {
                formResult: true,
              },
            },
            orderItems: {
              include: {
                ticket: {
                  include: {
                    category: {
                      include: {
                        dtcmSections: true,
                      },
                    },
                    dtcmTicketPrice: true,
                  },
                },
              },
            },
            event: true,
          },
        },
      },
    });
    return orders;
  } catch (error) {
    console.error("Error getting DTCM pending orders:", error);
    throw new Error("Failed to get DTCM pending orders");
  }
};

export const completeDTCMPendingOrder = async (order: any) => {
  try {
  } catch (error) {
    console.error("Error completing DTCM pending orders:", error);
    throw new Error("Failed to complete DTCM pending orders");
  }
};

export type DTCMOrdersSummaryRow = {
  productId: string;
  productName: string;
  priceTypeCode: string;
  priceCategoryCode: string;
  count: number;
};

type DtcmPriceWithTickets = {
  priceTypeId: string;
  name: string;
  typeCode: string;
  tickets?: Array<{
    bookableItem?: {
      translations?: Array<{ name: string }>;
    };
  }>;
};

function getSajilniTicketNameForDtcmPrice(price: DtcmPriceWithTickets): string {
  for (const ticket of price.tickets ?? []) {
    const ticketName = ticket.bookableItem?.translations?.[0]?.name;
    if (ticketName) {
      return ticketName;
    }
  }

  return price.name;
}

export const getDTCMOrdersSummaryForEvent = async (
  eventId: string
): Promise<DTCMOrdersSummaryRow[]> => {
  const prisma = reportingPrisma;
  const { result: categories } = await getDTCMTicketCategories(eventId);

  const summaryByProductId = new Map<string, DTCMOrdersSummaryRow>();

  for (const category of categories ?? []) {
    for (const price of category.dtcmTicketPrices ?? []) {
      summaryByProductId.set(price.priceTypeId, {
        productId: price.priceTypeId,
        productName: getSajilniTicketNameForDtcmPrice(price),
        priceTypeCode: price.typeCode,
        priceCategoryCode: category.code,
        count: 0,
      });
    }
  }

  const ticketIdToPriceTypeId = new Map<string, string>();
  for (const category of categories ?? []) {
    for (const price of category.dtcmTicketPrices ?? []) {
      for (const ticket of price.tickets ?? []) {
        ticketIdToPriceTypeId.set(ticket.id, price.priceTypeId);
      }
    }
  }

  const soldCountsByTicketId = await prisma.orderItem.groupBy({
    by: ["ticketId"],
    where: {
      ticketId: {
        not: null,
      },
      order: {
        eventId,
        status: OrderStatus.COMPLETED,
        dtcmOrder: {
          status: DTCMOrderStatus.COMPLETED,
        },
      },
      ticket: {
        dtcmTicketPriceId: {
          not: null,
        },
      },
    },
    _count: {
      _all: true,
    },
  });

  const unmappedTicketIds = soldCountsByTicketId
    .map((group) => group.ticketId)
    .filter(
      (ticketId): ticketId is string =>
        ticketId != null && !ticketIdToPriceTypeId.has(ticketId)
    );

  const unmappedTickets =
    unmappedTicketIds.length > 0
      ? await prisma.ticket.findMany({
          where: {
            id: {
              in: unmappedTicketIds,
            },
          },
          select: {
            id: true,
            bookableItem: {
              select: {
                translations: {
                  select: { name: true },
                  take: 1,
                },
              },
            },
            dtcmTicketPrice: {
              select: {
                priceTypeId: true,
                name: true,
                typeCode: true,
                category: {
                  select: {
                    code: true,
                  },
                },
              },
            },
          },
        })
      : [];

  const unmappedTicketById = new Map(
    unmappedTickets.map((ticket) => [ticket.id, ticket])
  );

  for (const { ticketId, _count } of soldCountsByTicketId) {
    if (!ticketId) {
      continue;
    }

    let priceTypeId = ticketIdToPriceTypeId.get(ticketId);
    const unmappedTicket = unmappedTicketById.get(ticketId);
    const ticketPrice = unmappedTicket?.dtcmTicketPrice;

    if (!priceTypeId && ticketPrice) {
      priceTypeId = ticketPrice.priceTypeId;
    }

    if (!priceTypeId) {
      continue;
    }

    const existing = summaryByProductId.get(priceTypeId);
    if (existing) {
      existing.count += _count._all;
      continue;
    }

    const sajilniTicketName =
      unmappedTicket?.bookableItem?.translations?.[0]?.name ??
      ticketPrice?.name ??
      priceTypeId;

    summaryByProductId.set(priceTypeId, {
      productId: priceTypeId,
      productName: sajilniTicketName,
      priceTypeCode: ticketPrice?.typeCode ?? "",
      priceCategoryCode: ticketPrice?.category?.code ?? "",
      count: _count._all,
    });
  }

  return Array.from(summaryByProductId.values()).sort(
    (left, right) => Number(left.productId) - Number(right.productId)
  );
};

export type DTCMOrderSummaryLineDetail = {
  orderId: string;
  orderNumber: number;
  orderItemId: string;
  dtcmBarcode: string | null;
  dtcmOrderId: string | null;
  buyerName: string;
  buyerEmail: string;
  completedAt: Date | null;
};

function extractBuyerFromFormResults(
  formResults: Array<{
    formResult: { surveyResult: unknown } | null;
  }>
) {
  const survey = (formResults[0]?.formResult?.surveyResult ?? {}) as Record<
    string,
    string
  >;
  const firstName = survey?.first_name || survey?.name?.split(" ")[0] || "";
  const lastName = survey?.last_name || survey?.name?.split(" ")[1] || "";
  const name =
    [firstName, lastName].filter(Boolean).join(" ") || survey?.name || "";

  return {
    buyerName: name,
    buyerEmail: survey?.email ?? "",
  };
}

export const getDTCMOrdersSummaryLineDetailsForEvent = async (
  eventId: string,
  productId: string
): Promise<DTCMOrderSummaryLineDetail[]> => {
  const orderItems = await prisma.orderItem.findMany({
    where: {
      order: {
        eventId,
        status: OrderStatus.COMPLETED,
        dtcmOrder: {
          status: DTCMOrderStatus.COMPLETED,
        },
      },
      ticket: {
        dtcmTicketPrice: {
          priceTypeId: productId,
        },
      },
    },
    select: {
      id: true,
      dtcmBarcode: true,
      order: {
        select: {
          id: true,
          number: true,
          completedAt: true,
          dtcmOrder: {
            select: {
              dtcmId: true,
            },
          },
          formResults: {
            select: {
              formResult: {
                select: {
                  surveyResult: true,
                },
              },
            },
          },
        },
      },
    },
    orderBy: {
      order: {
        completedAt: "desc",
      },
    },
  });

  return orderItems.map((orderItem) => {
    const { buyerName, buyerEmail } = extractBuyerFromFormResults(
      orderItem.order.formResults
    );

    return {
      orderId: orderItem.order.id,
      orderNumber: orderItem.order.number,
      orderItemId: orderItem.id,
      dtcmBarcode: orderItem.dtcmBarcode,
      dtcmOrderId: orderItem.order.dtcmOrder?.dtcmId ?? null,
      buyerName,
      buyerEmail,
      completedAt: orderItem.order.completedAt,
    };
  });
};
